/**
 * The attack-animation scene (`attack-animations` Phase 3): a small clock of self-removing effects
 * on the board's VFX layers, in the board's ticker-entry pattern. An effect is a node plus an
 * `update(age)` that redraws it and returns `false` once done, after which the scene destroys it.
 * Ported from the 0.2 prototype (`projects/attack-animations/prototype.html`), which used the same
 * shape so the picked looks would carry over nearly verbatim.
 *
 * The scene keeps its own clock rather than reading `performance.now()` per effect, so a crit's
 * hit-stop can freeze every live effect for a few frames at once.
 */
import { Container, Graphics } from "pixi.js";

export type V = { x: number; y: number };

export const TAU = Math.PI * 2;
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
export const easeOut = (p: number): number => 1 - (1 - p) * (1 - p);
export const easeOutCubic = (p: number): number => 1 - Math.pow(1 - p, 3);
export const easeIn = (p: number): number => p * p;
export const easeInOut = (p: number): number => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
export const v2 = (x: number, y: number): V => ({ x, y });
export const add = (a: V, b: V): V => v2(a.x + b.x, a.y + b.y);
export const sub = (a: V, b: V): V => v2(a.x - b.x, a.y - b.y);
export const mul = (a: V, k: number): V => v2(a.x * k, a.y * k);
export const len = (a: V): number => Math.hypot(a.x, a.y);
export const norm = (a: V): V => {
  const l = len(a) || 1;
  return v2(a.x / l, a.y / l);
};
export const lerp = (a: V, b: V, t: number): V => v2(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
export const perp = (a: V): V => v2(-a.y, a.x);
export const ang = (a: V): number => Math.atan2(a.y, a.x);
export const polar = (r: number, t: number): V => v2(Math.cos(t) * r, Math.sin(t) * r);
/** A point `t` along the quadratic Bézier `[p0, p1, p2]`. */
export const qbez = (curve: readonly [V, V, V], t: number): V => {
  const [p0, p1, p2] = curve;
  const u = 1 - t;
  return v2(u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x, u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y);
};
export const flat = (points: readonly V[]): number[] => points.flatMap((p) => [p.x, p.y]);

type Effect = { node: Container; start: number; update: (age: number) => boolean };

/** Where an effect draws: over the tokens (the default) or on the ground under them (scorches, shadows). */
export type FxLayer = "over" | "under";

export class FxScene {
  /** The scene clock, in ms; it stops during a hit-stop. */
  t = 0;
  private hitstop = 0;
  private effects: Effect[] = [];
  private events: { at: number; fn: () => void }[] = [];

  constructor(
    private readonly layers: { over: Container; under: Container },
    readonly rng: () => number = Math.random,
  ) {}

  /** Run `fn` `ms` from now on the scene clock. */
  at(ms: number, fn: () => void): void {
    this.events.push({ at: this.t + ms, fn });
  }

  /** Add a live effect; `update(age)` returns `false` when it is done. */
  add<N extends Container>(node: N, update: (age: number) => boolean, layer: FxLayer = "over"): N {
    this.layers[layer].addChild(node);
    this.effects.push({ node, start: this.t, update });
    return node;
  }

  /** A Graphics redrawn every frame for `life` ms: `draw(g, progress, age)`. */
  timed(life: number, draw: (g: Graphics, p: number, age: number) => void, layer: FxLayer = "over"): Graphics {
    const g = new Graphics();
    return this.add(
      g,
      (age) => {
        g.clear();
        draw(g, clamp01(age / life), age);
        return age < life;
      },
      layer,
    );
  }

  /** Freeze the clock for `ms` (a crit's hit-stop). */
  freeze(ms: number): void {
    this.hitstop = Math.max(this.hitstop, ms);
  }

  /** Whether anything is still playing or scheduled. */
  get busy(): boolean {
    return this.effects.length > 0 || this.events.length > 0;
  }

  /** Advance `dt` ms: fire due events in time order, then update every effect. */
  step(dt: number): void {
    let adv = Math.min(50, dt);
    if (this.hitstop > 0) {
      const use = Math.min(this.hitstop, adv);
      this.hitstop -= use;
      adv -= use;
    }
    this.t += adv;
    for (;;) {
      let idx = -1;
      let best = Infinity;
      for (let i = 0; i < this.events.length; i++) {
        const e = this.events[i]!;
        if (e.at <= this.t && e.at < best) {
          best = e.at;
          idx = i;
        }
      }
      if (idx < 0) break;
      const [ev] = this.events.splice(idx, 1);
      ev!.fn();
    }
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const e = this.effects[i]!;
      if (!e.update(this.t - e.start)) {
        this.effects.splice(i, 1);
        e.node.destroy({ children: true });
      }
    }
  }

  /** Drop every live effect and pending event (a snap jumps past the flourish). */
  clear(): void {
    for (const e of this.effects) e.node.destroy({ children: true });
    this.effects = [];
    this.events = [];
    this.hitstop = 0;
  }
}

// ── Shared effect primitives (each is one scene effect) ─────────────────────────────────────────

type Stroke = { color: number; alpha?: number };

export function ring(s: FxScene, o: { at: V; r0: number; r1: number; w?: number; life?: number; layer?: FxLayer } & Stroke): void {
  const { at, r0, r1, w = 3, color, life = 300, alpha = 1, layer } = o;
  s.timed(life, (g, p) => {
    g.circle(at.x, at.y, r0 + (r1 - r0) * easeOut(p)).stroke({ width: Math.max(0.5, w * (1 - p * 0.6)), color, alpha: alpha * (1 - p) });
  }, layer);
}

export function disc(s: FxScene, o: { at: V; r0: number; r1: number; life?: number; layer?: FxLayer } & Stroke): void {
  const { at, r0, r1, color, life = 200, alpha = 1, layer } = o;
  s.timed(life, (g, p) => {
    g.circle(at.x, at.y, Math.max(0.1, r0 + (r1 - r0) * easeOut(p))).fill({ color, alpha: alpha * (1 - p) });
  }, layer);
}

export function sparks(s: FxScene, o: { at: V; n?: number; dist: number; len?: number; life?: number; dir?: number; spread?: number; w?: number } & Stroke): void {
  const { at, n = 8, color, dist, len: ln = 7, life = 260, dir = 0, spread = TAU, w = 1.6, alpha = 1 } = o;
  const sp = Array.from({ length: n }, () => ({ a: dir + (s.rng() - 0.5) * spread, k: 0.5 + s.rng() * 0.6 }));
  s.timed(life, (g, p) => {
    for (const q of sp) {
      const d = dist * q.k * easeOut(p);
      const l = ln * (1 - p) + 1;
      const u = polar(1, q.a);
      g.moveTo(at.x + u.x * Math.max(0, d - l), at.y + u.y * Math.max(0, d - l)).lineTo(at.x + u.x * d, at.y + u.y * d);
    }
    g.stroke({ width: w, color, alpha: alpha * (1 - p * 0.7), cap: "round" });
  });
}

export function puffs(s: FxScene, o: { at: V; n?: number; r?: number; grow?: number; spread?: number; life?: number; drift?: V; layer?: FxLayer } & Partial<Stroke>): void {
  const { at, n = 5, color = 0x8f877a, r = 4, grow = 8, spread = 10, life = 600, alpha = 0.35, drift = v2(0, -6), layer } = o;
  const offs = Array.from({ length: n }, () => v2((s.rng() - 0.5) * 2 * spread, (s.rng() - 0.5) * 2 * spread));
  s.timed(life, (g, p) => {
    for (const q of offs) g.circle(at.x + q.x * (1 + p * 0.8) + drift.x * p, at.y + q.y * (1 + p * 0.8) + drift.y * p, r + grow * easeOut(p));
    g.fill({ color, alpha: alpha * (1 - p) });
  }, layer);
}

export function chunks(s: FxScene, o: { at: V; n?: number; size?: number; dist: number; life?: number; dir?: number; spread?: number; color: number }): void {
  const { at, n = 7, color, size = 3, dist, life = 420, dir = 0, spread = TAU } = o;
  const cs = Array.from({ length: n }, () => ({ a: dir + (s.rng() - 0.5) * spread, k: 0.4 + s.rng() * 0.7, sz: size * (0.6 + s.rng() * 0.8) }));
  s.timed(life, (g, p) => {
    for (const q of cs) {
      const d = dist * q.k * easeOutCubic(p);
      const u = polar(d, q.a);
      const z = q.sz * (1 - p * 0.5);
      g.rect(at.x + u.x - z / 2, at.y + u.y - z / 2, z, z);
    }
    g.fill({ color, alpha: 1 - easeIn(p) });
  });
}

export function embers(s: FxScene, o: { at: V; n?: number; life?: number; color: number }): void {
  const { at, n = 7, color, life = 650 } = o;
  const es = Array.from({ length: n }, () => ({ ox: (s.rng() - 0.5) * 22, oy: (s.rng() - 0.5) * 14, ph: s.rng() * TAU, rise: 14 + s.rng() * 16 }));
  s.timed(life, (g, p) => {
    for (const e of es) g.circle(at.x + e.ox + Math.sin(e.ph + p * 9) * 3, at.y + e.oy - e.rise * p, 1.6);
    g.fill({ color, alpha: (1 - p) * (0.6 + 0.4 * Math.sin(p * 40)) });
  });
}

/** A projectile from `from` to `to` (or along `path(p)`), drawn by `draw` each frame (with the eased progress and the age); gone on arrival. */
export function mover(s: FxScene, o: { from: V; to: V; ms: number; path?: (p: number) => V; ease?: (p: number) => number; draw: (g: Graphics, pos: V, at: { p: number; age: number }) => void; layer?: FxLayer }): void {
  const { from, to, ms, path, ease = (p: number) => p, draw, layer } = o;
  s.timed(ms, (g, p, age) => {
    const e = ease(p);
    draw(g, path ? path(e) : lerp(from, to, e), { p: e, age });
  }, layer);
}
